import { flushSync } from "react-dom";
import { placeOf, type RouteName } from "../routes.js";

/**
 * THE PLACE ON THE DOCUMENT ROOT (2026-11-02). `data-place="study"` or
 * `data-place="book"` on `<html>`, from the route alone (routes.ts
 * `placeOf`), and no attribute for a route that is neither. On the root
 * rather than on the shell, because three papers render through portals to
 * the body (the guest paper, the terms paper, the erasure dialog), the
 * settings sheet renders outside the shell, and the body's own background
 * has to follow too; anything hung on a shell element would miss them. A
 * route with no place reads the root tokens, which since the shared ink and
 * paper (2026-11-03) are the book's Flax and ink: the sign-in card, the
 * reading pages, the endgame and the settings sheet all take them.
 *
 * Called once before the first paint (main.tsx), so a cold load of the study
 * never shows one light frame, then kept current by the app on every route
 * change. Nothing here is a style: tokens.css is where a place becomes a
 * colour.
 */
export const PLACE_ATTRIBUTE = "data-place";

export function applyPlace(route: RouteName, root: Element = globalThis.document.documentElement): void {
  const place = placeOf(route);
  if (place === null) root.removeAttribute(PLACE_ATTRIBUTE);
  else root.setAttribute(PLACE_ATTRIBUTE, place);
}

/** The one browser API this module reaches for, typed here so an older lib.dom does not decide whether it exists. */
interface DocumentWithViewTransition {
  startViewTransition?: (update: () => void) => unknown;
}

/**
 * THE THRESHOLD (2026-11-02). A route change that crosses from one place to
 * the other — the study to the book, or back — is a large change of
 * brightness, and the design asks that it be softened without a white flash
 * and stay understandable with motion off. So: when the browser has view
 * transitions and nothing has asked for stillness, the change is applied
 * inside one, which cross-fades the whole page (its duration is the
 * stylesheet's `--pc-dur-slow`, 1ms under reduced motion). The attribute is
 * written inside the same callback, so the new snapshot already wears the
 * new place; the app's own effect writes the same value again afterwards
 * and changes nothing. A change within one place, a browser without the
 * API, or a reader who asked for stillness: the change is applied at once,
 * exactly as before.
 */
export function acrossThePlace(from: RouteName, to: RouteName, apply: () => void): void {
  const doc = globalThis.document as Document & DocumentWithViewTransition;
  const crossing = placeOf(from) !== placeOf(to);
  const still = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  if (!crossing || still || typeof doc.startViewTransition !== "function") {
    apply();
    return;
  }
  doc.startViewTransition(() => {
    flushSync(apply);
    applyPlace(to);
  });
}
